import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Upload, Compass, FileText, ShieldCheck, ListChecks,
  GitBranch, Scale, ServerCog, Activity, Gauge, Landmark, BarChart3,
  Boxes, FlaskConical, ScrollText, PackageCheck, Rocket, Search,
} from "lucide-react";
import Layout, { PageContainer } from "../components/ui/Layout";
import { cn } from "../lib/utils";

/* Section anchors for the sticky table of contents. */
const SECTIONS = [
  { id: "overview", label: "What Paxley is" },
  { id: "capabilities", label: "The six capabilities" },
  { id: "start", label: "Getting started" },
  { id: "lifecycle", label: "The scan lifecycle" },
  { id: "workspace", label: "The assurance workspace" },
  { id: "views", label: "The 20 views" },
  { id: "finding", label: "Reading a finding" },
  { id: "coverage-matrix", label: "Language coverage" },
  { id: "glossary", label: "Field glossary" },
  { id: "reports", label: "Reports" },
  { id: "api", label: "API access" },
  { id: "honesty", label: "The honesty principle" },
];

const VIEW_GROUPS: { group: string; views: { name: string; icon: React.ElementType; desc: string }[] }[] = [
  {
    group: "Executive overview",
    views: [
      { name: "Application", icon: Gauge, desc: "Risk score, release decision, and open exposure for one application on a single screen — the ten-second posture answer." },
      { name: "Portfolio", icon: Boxes, desc: "Every application with findings, criticals, and evidence freshness. Where risk concentrates across your estate." },
      { name: "Capabilities", icon: ListChecks, desc: "Which analysis capabilities are demonstrated with evidence versus still requiring validation — the honesty contract." },
    ],
  },
  {
    group: "Evidence & architecture",
    views: [
      { name: "Coverage", icon: Compass, desc: "What was scanned, what was excluded, the commit it's bound to, and the pipeline's own stated limitations." },
      { name: "Architecture", icon: ServerCog, desc: "Components ranked by where findings concentrate, and the attack surfaces carrying the most critical risk." },
    ],
  },
  {
    group: "Findings & remediation",
    views: [
      { name: "Findings", icon: Search, desc: "The normalized inventory across every capability, filterable by domain, severity, and status." },
      { name: "Evidence", icon: FileText, desc: "One finding in full: the code, the source→sink data-flow, CWE, CVSS, and rule-specific remediation." },
      { name: "Remediation", icon: PackageCheck, desc: "Prioritized fixes with the published fix version where one exists — the quick wins." },
      { name: "30 / 60 / 90", icon: ListChecks, desc: "The remediation sequenced into accountable phases from measured severity and fix availability." },
    ],
  },
  {
    group: "Supply chain & engineering",
    views: [
      { name: "Supply chain", icon: PackageCheck, desc: "Vulnerable dependencies with CVEs and fix versions, plus the commit-bound SBOM." },
      { name: "Licenses", icon: Scale, desc: "Each dependency's license family and policy position — approved, review, restricted, or unresolved." },
      { name: "Cloud", icon: ServerCog, desc: "Infrastructure-as-code and container findings from the deployment configuration." },
      { name: "Quality", icon: Activity, desc: "Files ranked by finding concentration — where engineering attention pays off." },
      { name: "Dev health", icon: GitBranch, desc: "Commits, contributors, bus factor, and ownership concentration — key-person risk." },
    ],
  },
  {
    group: "Governance & policy",
    views: [
      { name: "Compliance", icon: Landmark, desc: "Measured evidence mapped to control areas — measured, gap, or not assessed." },
      { name: "Benchmark", icon: BarChart3, desc: "Position against your own scanned portfolio, metric by metric (not an industry claim)." },
      { name: "PR guard", icon: ShieldCheck, desc: "Policy gates evaluated against the evidence and the merge decision they produce." },
    ],
  },
  {
    group: "Reports & proof-of-value",
    views: [
      { name: "Reports", icon: ScrollText, desc: "Commit-bound reports in PDF/HTML/JSON/CSV, generated from an immutable snapshot with SHA-256 integrity." },
      { name: "Deployment", icon: Rocket, desc: "Release-gate status, artifact provenance, and formally accepted risks." },
      { name: "Proof-of-value", icon: FlaskConical, desc: "Measured coverage against replacement criteria, with the validation path stated honestly." },
    ],
  },
];

const GLOSSARY: { term: string; def: string }[] = [
  { term: "Severity", def: "Critical / High / Medium / Low / Info. For dependency findings it is banded from the advisory's own CVSS score; for code findings from the rule's published security severity — measured, not guessed." },
  { term: "Lines of code", def: "Physical lines counted from the working tree at scan time, split into code, comment and blank per language. Vendored directories, minified bundles, generated files and lock files are excluded, and the exclusions are reported alongside the count so the figure is auditable. Exposed as codeSize in the API." },
  { term: "Findings per KLOC", def: "Finding density — findings located in programming-language files, divided by the code lines in those same languages. Dependency and licence findings are excluded because they describe imported packages rather than the lines counted; they are reported separately. Exposed as findingDensity in the API." },
  { term: "CVSS score & vector", def: "The Common Vulnerability Scoring System base score (0–10) and the vector string it was computed from. Shown on the finding as the printable basis for its severity." },
  { term: "EPSS", def: "Exploit Prediction Scoring System — the probability (0–1) that a vulnerability will be exploited in the wild, from the public FIRST dataset. Feeds the risk score." },
  { term: "KEV", def: "Known Exploited Vulnerabilities — whether the CVE appears in CISA's catalog of vulnerabilities exploited in the wild. Checked on every scan; gates the 0–30-day plan." },
  { term: "Reachability", def: "Whether tainted data can actually reach the vulnerable code. Measured for Go dependencies (call-graph analysis); stated as 'not measured' elsewhere rather than estimated." },
  { term: "Contextual risk score", def: "An aggregate score derived deterministically from severity, exploit probability, known-exploited status, reachability, and finding age — the formula source is printed, so it is auditable." },
  { term: "Priority evidence", def: "A finding carrying a demonstrated priority signal: known-exploited, materially likely to be exploited, measured reachable, or top-ranked — a meaningful subset, not every finding." },
  { term: "Evidence chain", def: "The step-by-step path to the conclusion. For code: untrusted source → flows through → reaches sink → weakness classified. For dependencies: package detected → advisory matched → fix available." },
  { term: "Validation steps", def: "How a reviewer independently confirms a finding — reproduce it, apply the fix, and confirm it no longer appears in the next scan." },
  { term: "CWE", def: "Common Weakness Enumeration — the class of weakness (e.g. CWE-94: Code Injection). Shown with its human-readable name and mapped to remediation guidance." },
  { term: "Corroborated", def: "Two independent analysis engines flagged the same weakness at the same location. The duplicates are merged into one finding with a corroboration badge — one real issue, double-confirmed." },
  { term: "Detection confidence", def: "The confidence the detecting rule declares for itself (high / medium / low), when the engine publishes one. Filterable on the Findings view; absent means the rule does not state a confidence." },
  { term: "OWASP Top 10", def: "The standard 2021 web-application risk categories (A01–A10). Code findings carry their category from the detecting rule, and the Findings view can group by it." },
  { term: "Capability", def: "The analysis type that produced a finding — source-code security, dependency & supply-chain, secret detection, infrastructure-as-code, container security, or license governance. We describe capabilities, never tool brands." },
  { term: "Evidence freshness", def: "How old the scan behind a view is. Evidence decays; a stale scan is labeled as such rather than presented as current posture." },
  { term: "Not measured", def: "A field the scan did not observe. Paxley labels it explicitly and never infers a value — the discipline that makes the output defensible." },
];

function ApiBlock({ children, label }: { children: string; label?: string }) {
  return (
    <div className="mt-2">
      {label && (
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">{label}</div>
      )}
      <pre className="overflow-x-auto rounded-xl border border-border/70 bg-surface2/50 px-4 py-3 text-[12.5px] leading-6 text-text2">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function FieldTable({ rows }: { rows: [string, string][] }) {
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-border/70">
      <dl>
        {rows.map(([field, desc], i) => (
          <div key={field} className={cn("grid grid-cols-1 gap-1 px-4 py-2.5 sm:grid-cols-[200px_1fr] sm:gap-4", i % 2 ? "bg-surface1" : "bg-surface2/40")}>
            <dt className="font-mono text-[12px] font-semibold text-brand">{field}</dt>
            <dd className="text-[12.5px] leading-6 text-muted">{desc}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Shot({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="my-5 overflow-hidden rounded-xl border border-border/70 bg-surface1 shadow-sm">
      <img src={src} alt={alt} className="w-full" loading="lazy" />
      <figcaption className="border-t border-border/60 bg-surface2/40 px-4 py-2 text-xs text-muted">{caption}</figcaption>
    </figure>
  );
}

function H({ id, icon: Icon, children }: { id: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <h2 id={id} className="mb-3 mt-10 flex scroll-mt-6 items-center gap-2.5 text-xl font-bold tracking-tight text-text first:mt-0">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand/12 text-brand ring-1 ring-brand/20">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      {children}
      <span aria-hidden className="ml-1 h-[3px] flex-1 rounded-full bg-gradient-to-r from-brand/40 to-transparent" />
    </h2>
  );
}

export default function Guide() {
  const [active, setActive] = useState("overview");
  return (
    <Layout>
      <PageContainer>
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/12 text-brand ring-1 ring-brand/20">
            <BookOpen className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text">Product guide</h1>
            <p className="mt-0.5 text-sm text-muted">How to use the Paxley Code Security Platform — every view, every field, with examples.</p>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sticky table of contents */}
          <nav className="sticky top-6 hidden h-fit w-52 flex-none lg:block" aria-label="Guide contents">
            <div className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.14em] text-muted">On this page</div>
            <ul className="space-y-0.5">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={() => setActive(s.id)}
                    className={cn(
                      "block rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors",
                      active === s.id ? "bg-brand/10 text-brand" : "text-text2 hover:bg-surface2 hover:text-text"
                    )}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="min-w-0 flex-1 max-w-3xl text-[15px] leading-7 text-text2">
            {/* Overview */}
            <H id="overview" icon={BookOpen}>What Paxley is</H>
            <p>
              The Paxley Code Security Platform turns repository evidence into security
              decisions and into client-ready reports. You point it at code — a repository
              or an uploaded archive — and it runs six analysis capabilities, normalizes
              everything into one evidence model, and binds every conclusion to an exact
              commit and scan.
            </p>
            <p className="mt-3">Three ideas explain the whole product:</p>
            <ul className="mt-2 space-y-2">
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span><strong className="text-text">One evidence model.</strong> Every view is a lens over the same store of findings, so all views agree with each other.</span></li>
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span><strong className="text-text">Commit binding.</strong> Every number traces to an exact commit and scan, shown in the banner on every page.</span></li>
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span><strong className="text-text">The honesty principle.</strong> Anything not measured is labeled "not measured" — never inferred. That is what makes the output defensible.</span></li>
            </ul>

            {/* Capabilities */}
            <H id="capabilities" icon={ListChecks}>The six analysis capabilities</H>
            <p>
              Every scan runs six independent analysis capabilities in parallel and normalizes their
              output into one evidence model. We describe capabilities, never tool brands — but each is
              backed by mature, industry-standard engines that we monitor and update.
            </p>
            <div className="mt-4 space-y-3">
              {[
                { name: "Source-code security (SAST)", cwe: "Injection, XSS, SSRF, path traversal, insecure crypto, deserialization…", detail: "Two depths: deep semantic analysis traces tainted data from its source to a vulnerable sink (full data-flow evidence); pattern-based analysis applies curated security rules. Coverage per language is in the matrix below." },
                { name: "Dependency & supply-chain (SCA)", cwe: "Known-vulnerable open-source packages", detail: "Resolves manifests and lockfiles (npm, PyPI, Maven/Gradle, Go, RubyGems, Composer, NuGet, Cargo, and more) against public advisory databases. Where a lockfile is committed, or a .NET project has been restored, the resolved graph is exact. Where only manifests exist — the common case for .NET projects using PackageReference and central package management — the declared packages are read and the transitive graph is resolved from the package registry; findings record which of the two applies. If an ecosystem cannot be resolved at all, the assessment says so rather than reporting an empty list. Each finding carries the CVE, CVSS, published fix version, semver upgrade effort, and where the package is actually imported — and a CycloneDX + SPDX SBOM is produced on every scan." },
                { name: "Secret detection", cwe: "Hard-coded credentials, API keys, private keys", detail: "Scans every file plus committed git history — a secret removed from the working tree but still in history is still compromised. Severity is graded by credential type." },
                { name: "Infrastructure-as-code (IaC)", cwe: "Misconfigured cloud & Kubernetes", detail: "Reviews Terraform and Kubernetes manifests for insecure defaults, exposed resources, and policy violations before they reach production." },
                { name: "Container security", cwe: "Insecure Dockerfiles & container config", detail: "Inspects Dockerfiles and container configuration for insecure base images, root execution, and misconfiguration." },
                { name: "License governance", cwe: "Copyleft / restrictive license exposure", detail: "Classifies each dependency's license into policy families (permissive, weak-copyleft, strong-copyleft, unknown) with per-package obligations. (License classification is not legal advice.)" },
              ].map((c) => (
                <div key={c.name} className="rounded-xl border border-border/70 bg-surface1 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-[14px] font-semibold text-text">{c.name}</div>
                    <div className="text-[11.5px] text-muted">{c.cwe}</div>
                  </div>
                  <p className="mt-1.5 text-[13px] leading-6 text-muted">{c.detail}</p>
                </div>
              ))}
            </div>

            {/* Getting started */}
            <H id="start" icon={Upload}>Getting started</H>
            <p>There are three ways to scan an application — no setup or installation required for the first two:</p>
            <ul className="mt-2 space-y-2">
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span><strong className="text-text">From a Git URL.</strong> Paste any public repository URL on the <Link to="/upload" className="text-brand hover:underline">Upload Code</Link> page. Paxley clones it read-only, scans it, and cleans up.</span></li>
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span><strong className="text-text">Upload a zip.</strong> For code you can't put in a public repo, upload a ZIP or TAR.GZ archive.</span></li>
              <li className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand" /><span><strong className="text-text">Connect GitHub.</strong> Install the GitHub App for automatic scans on a schedule and on every pull request.</span></li>
            </ul>
            <Shot src="/guide-img/upload.png" alt="Upload Code page" caption="Upload Code — scan a public repository by URL or upload a zip, no GitHub required." />
            <p>A scan runs the full analysis in a few minutes. When it completes, the assurance workspace is populated and reports can be generated.</p>

            {/* Scan lifecycle */}
            <H id="lifecycle" icon={Activity}>The scan lifecycle</H>
            <p>
              A scan moves through clear phases. The <span className="font-mono text-[12.5px] text-text">status</span> endpoint
              (and the Repositories page) reflect each one, so you always know where a scan is:
            </p>
            <ol className="mt-3 space-y-2.5">
              {[
                ["Cloning / extracting", "The repository is cloned read-only (bounded history) or the uploaded archive is extracted into an isolated workspace."],
                ["Scanning", "All six capabilities run in parallel. Deep semantic analysis and dependency resolution are the longest steps."],
                ["Processing", "Raw results are normalized into the evidence model — deduplicated, scored, classified by CWE, and enriched (fix versions, usage sites, corroboration)."],
                ["Complete", "Findings, composition, supply chain, and the SBOM are persisted against the commit. The workspace populates and reports can be generated. Scanned source is then discarded — only the findings are retained."],
              ].map(([phase, desc], i) => (
                <li key={phase} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand/12 text-[12px] font-bold text-brand ring-1 ring-brand/20">{i + 1}</span>
                  <span><strong className="text-text">{phase}.</strong> {desc}</span>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-[13px] text-muted">
              A full scan typically takes 3–4 minutes; large repositories or a compiled C/C++ build can take longer.
              Re-scanning an application produces a new scan bound to the new commit, and the workspace shows the delta.
            </p>

            {/* Workspace */}
            <H id="workspace" icon={Compass}>The assurance workspace</H>
            <p>
              The <Link to="/assurance" className="text-brand hover:underline">Assurance</Link> workspace is 20 views over one evidence model, grouped in the left rail.
              The banner at the top of every view is the anchor: the application, branch, the exact
              commit, the scan that produced the evidence, and how fresh it is. Every number below is bound to it.
            </p>
            <Shot src="/guide-img/application.png" alt="Application posture view" caption="Application — the executive posture: contextual risk, the release decision, and open exposure by severity." />
            <p>
              The Portfolio view zooms out to every application you've scanned, with findings, criticals,
              and a freshness badge on each — the triage surface for an estate.
            </p>
            <Shot src="/guide-img/portfolio.png" alt="Portfolio view" caption="Portfolio — risk and evidence freshness across every scanned application." />

            {/* Views */}
            <H id="views" icon={ListChecks}>The 20 views</H>
            <p className="mb-4">Each view is a lens on the same evidence. Here's what every one is for:</p>
            <div className="space-y-6">
              {VIEW_GROUPS.map((g) => (
                <div key={g.group}>
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-muted">{g.group}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {g.views.map((v) => (
                      <div key={v.name} className="rounded-xl border border-border/70 bg-surface1 p-3">
                        <div className="flex items-center gap-2 text-[13.5px] font-semibold text-text">
                          <v.icon className="h-4 w-4 flex-none text-brand" />
                          {v.name}
                        </div>
                        <p className="mt-1 text-[12.5px] leading-5 text-muted">{v.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Reading a finding */}
            <H id="finding" icon={FileText}>Reading a finding</H>
            <p>
              The Findings inventory lists every result in one consistent format — severity, title,
              capability, location, and status — with filters by domain, severity, status, and
              detection confidence, plus a <strong className="text-text">Group by OWASP Top 10</strong> toggle
              that organizes code findings by weakness category. When two independent analysis
              engines flag the same weakness at the same location, they are merged into one finding
              marked <strong className="text-text">corroborated</strong> — one real issue, double-confirmed.
            </p>
            <Shot src="/guide-img/findings.png" alt="Findings inventory" caption="Findings — the normalized inventory across every analysis capability, with filters." />
            <p>
              Opening one finding shows the full evidence. For a code finding that means:
              the exact code, the <strong className="text-text">source→sink data-flow</strong> (where tainted
              input enters and where it reaches the vulnerable sink), the <strong className="text-text">CWE</strong> with its
              name, the <strong className="text-text">CVSS</strong> basis, and a rule-specific remediation. For a dependency it
              shows the package, the advisory, the CVSS score, and the published fix version.
            </p>
            <Shot src="/guide-img/evidence.png" alt="Finding evidence" caption="Evidence — one finding in full: code, data-flow, CWE, CVSS, and remediation." />
            <p>
              Every finding also carries <strong className="text-text">validation steps</strong> — how to reproduce and
              confirm the fix — so the conclusion can be independently verified rather than trusted.
            </p>
            <p className="mt-4 mb-1 font-semibold text-text">The anatomy of a finding</p>
            <p className="text-[13.5px] text-muted">Every finding — whether it appears in the dashboard or comes back from the API — carries the same structured fields:</p>
            <FieldTable rows={[
              ["title, severity, status", "The human-readable summary, its Critical→Info band, and whether it's open, fixed, or suppressed."],
              ["domain, capability, tool", "Which analysis produced it (code security, dependency, secret, IaC, license) — capability language, never a tool brand."],
              ["location", "Exact file, line, and column. Code findings also carry the vulnerable code snippet and surrounding context."],
              ["cwe, cweName, owasp", "Weakness classification with its readable name (e.g. CWE-89: SQL Injection) and OWASP Top-10 category."],
              ["cve, cvssScore, cvssVector", "For dependency findings: the advisory id and the CVSS base score plus the vector it was computed from."],
              ["dataflow", "For code findings: the ordered source→sink steps showing exactly how tainted input reaches the sink."],
              ["fixedVersion, upgradeJump, upgradeEffort", "For dependencies: the first patched version, the semver distance (patch/minor/major), and the resulting low/medium/high upgrade effort."],
              ["usageSites", "How many source files actually import a vulnerable package — measured, so you can tell a widely-used dependency from a transitive one."],
              ["recommendation", "The concrete remediation — the rule's own guidance, or curated per-CWE guidance."],
              ["references", "Canonical advisory and rule-documentation links (OSV, NVD, rule pages)."],
              ["kev, epss, reachability", "Known-exploited flag, exploit-probability score, and (where measured) whether tainted data can reach the code."],
              ["corroboratedBy, confidence", "Engines that independently confirmed the same weakness, and the rule's own declared confidence."],
              ["evidenceChain, validationSteps", "The step-by-step path to the conclusion, and how to independently reproduce and confirm the fix."],
            ]} />
            <Shot src="/guide-img/supply-chain.png" alt="Supply chain view" caption="Supply chain — vulnerable dependencies with CVEs and fix versions, plus the SBOM." />

            {/* Language coverage */}
            <H id="coverage-matrix" icon={Boxes}>Language &amp; capability coverage</H>
            <p>
              Two analysis depths apply per language: <strong className="text-text">deep semantic
              analysis</strong> (source→sink data-flow tracing) and <strong className="text-text">pattern-based
              security analysis</strong> (curated security rules). Four capabilities are
              language-agnostic and cover every stack: dependency &amp; supply-chain analysis,
              secret detection (including git history), infrastructure-as-code &amp; container
              configuration, and license governance.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border/70">
              <table className="w-full min-w-[560px] text-[13px]">
                <thead>
                  <tr className="bg-surface2/60 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-text2">
                    <th className="px-4 py-2.5">Language</th>
                    <th className="px-4 py-2.5">Deep semantic (data-flow)</th>
                    <th className="px-4 py-2.5">Pattern-based security</th>
                    <th className="px-4 py-2.5">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50 text-text2">
                  {[
                    ["JavaScript / TypeScript", "Yes", "Yes", "Full depth, no build required"],
                    ["Python", "Yes", "Yes", "Full depth, no build required"],
                    ["Java", "Yes", "Yes", "Full depth, no build required"],
                    ["Go", "Yes", "Yes", "Plus dependency reachability analysis"],
                    ["Ruby", "Yes", "Yes", "Full depth, no build required"],
                    ["PHP", "—", "Yes", "Curated security ruleset"],
                    ["C# / .NET", "Yes", "Yes", "Full depth, no build required — including Razor views and legacy .NET Framework"],
                    ["C / C++", "Yes", "—", "Build-integrated deep analysis (Linux builds), buildless fallback"],
                    ["ColdFusion / CFML (Adobe ColdFusion, Lucee)", "—", "Yes", "Curated security ruleset for tag and script syntax; committed JavaScript and Java library advisories; engine end-of-support and known-exploited advisories"],
                    ["Kotlin, Swift, Rust, Scala", "—", "—", "Covered by the language-agnostic capabilities below"],
                  ].map(([lang, deep, pattern, notes]) => (
                    <tr key={lang}>
                      <td className="px-4 py-2.5 font-semibold text-text">{lang}</td>
                      <td className="px-4 py-2.5">{deep}</td>
                      <td className="px-4 py-2.5">{pattern}</td>
                      <td className="px-4 py-2.5 text-muted">{notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3">
              <strong className="text-text">Every stack, regardless of language:</strong> dependency
              vulnerabilities from manifests and lockfiles (npm, PyPI, Maven, Go, RubyGems,
              Composer, NuGet, Cargo and more), each labelled with whether its
              dependency graph was resolved exactly or from the package registry; libraries
              committed as files (a vendored jQuery or Bootstrap build, a JAR in lib/) are
              identified from the files themselves and checked the same way; secret detection across all files and committed
              git history, infrastructure-as-code and container configuration review, and
              license classification. Support for additional languages and tools can typically
              be added within two to three weeks on request.
            </p>

            {/* Glossary */}
            <H id="glossary" icon={Search}>Field glossary</H>
            <p className="mb-4">The terms you'll see across the workspace, defined:</p>
            <dl className="overflow-hidden rounded-xl border border-border/70">
              {GLOSSARY.map((g, i) => (
                <div key={g.term} className={cn("grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-[180px_1fr] sm:gap-4", i % 2 ? "bg-surface1" : "bg-surface2/40")}>
                  <dt className="text-[13.5px] font-semibold text-text">{g.term}</dt>
                  <dd className="text-[13px] leading-6 text-muted">{g.def}</dd>
                </div>
              ))}
            </dl>

            {/* Reports */}
            <H id="reports" icon={ScrollText}>Reports</H>
            <p>
              The Reports view turns the evidence into a deliverable. A guided wizard lets you choose the
              report type, the output formats, and set the prepared-for name, classification, and which
              sections to include. There are nine report types, each shaped for a different audience:
            </p>
            <FieldTable rows={[
              ["comprehensive", "Everything — the full assessment across every section. The default client deliverable."],
              ["executive", "Leadership-ready posture narrative: risk, the release decision, and top exposures, without the finding-level detail."],
              ["technical", "The complete normalized finding inventory with evidence, for the engineers doing the fixing."],
              ["exploitability", "Findings ranked by demonstrated exploit signal — known-exploited, high EPSS, reachable."],
              ["architecture", "Component-level exposure: where findings concentrate and which attack surfaces carry the risk."],
              ["supply-chain", "Dependency and license posture plus the SBOM."],
              ["compliance", "Measured evidence mapped to control areas, without certification claims."],
              ["remediation", "The prioritized, evidence-ranked 30/60/90 fix plan."],
              ["findings", "A lean inventory export — useful as the CSV/JSON/XML/SARIF data feed."],
            ]} />
            <p className="mt-4">
              Every type is available in <span className="font-mono text-[12.5px] text-text">PDF, HTML, JSON, CSV, and XML</span>,
              plus <span className="font-mono text-[12.5px] text-text">CycloneDX</span> and <span className="font-mono text-[12.5px] text-text">SPDX</span> SBOMs
              and a <span className="font-mono text-[12.5px] text-text">SARIF 2.1.0</span> export for importing findings into your own tooling
              (IDEs, aggregation platforms, code-scanning dashboards).
            </p>
            <Shot src="/guide-img/reports.png" alt="Reports view" caption="Reports — generate commit-bound, SHA-256-verified deliverables in every format." />
            <p>
              Generation captures an <strong className="text-text">immutable snapshot</strong>: the report renders from frozen
              evidence, so later scans never silently change a delivered document. Every artifact carries a
              SHA-256 integrity hash, so months later you can prove a PDF is the one that was issued.
              A completed report can also be <strong className="text-text">shared</strong> as a read-only link
              anyone can open without a login — valid 30 days and revocable.
            </p>

            {/* API */}
            <H id="api" icon={ServerCog}>API access</H>
            <p>
              Everything in this dashboard is available over HTTPS as JSON. Trigger scans, poll status,
              pull the complete findings evidence, and generate or download every export format — so
              Paxley slots directly into your own pipeline, notebook, or reporting engine. It's the same
              API the dashboard itself runs on, so anything you can see, you can pull.
            </p>
            <p className="mt-3 text-[14px]">
              Authentication is one header — your workspace API key on every request:
            </p>
            <ApiBlock>{`x-paxley-key: <your-api-key>`}</ApiBlock>
            <p className="mt-3 text-[14px]">The core loop most integrations need — scan, wait, pull:</p>
            <ApiBlock>{`curl -X POST https://api.paxleysoftware.com/v1/scan-url -H "x-paxley-key: $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"gitUrl": "https://github.com/your-org/repo", "projectName": "svc"}'
# -> { "repo_id": "...", "job_id": "..." }   (use these ids in the next calls)

curl https://api.paxleysoftware.com/v1/jobs/$JOB_ID/status  -H "x-paxley-key: $KEY"
curl https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID -H "x-paxley-key: $KEY"`}</ApiBlock>
            <p className="mt-4 text-[14px]">
              The assessment payload carries the repository&rsquo;s size and defect density alongside the findings, so
              metrics normalised by code size need no second call:
            </p>
            <ApiBlock>{`# lines of code per language (code / comment / blank, vendored + generated excluded)
curl -s https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID \
  -H "x-paxley-key: $KEY" | jq '.data.codeSize'

# findings per KLOC and per 10k LOC, already divided
curl -s https://api.paxleysoftware.com/v1/assurance/repos/$REPO_ID \
  -H "x-paxley-key: $KEY" | jq '.data.findingDensity'`}</ApiBlock>
            <p className="mt-3 text-[13.5px] leading-6 text-muted">
              Density counts only findings that live in programming-language files, over the code lines of those same
              languages. Dependency and licence findings are reported separately under <code>excludedFromDensity</code>:
              they are properties of imported packages, not of the lines counted, so including them would make the
              figure track dependency count rather than code size.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand/25 bg-brand/5 p-4">
              <ServerCog className="h-5 w-5 flex-none text-brand" />
              <div className="min-w-0 flex-1 text-[13.5px] leading-6 text-text2">
                <strong className="text-text">Full API documentation</strong> — every endpoint, its parameters and payloads,
                where each id comes from, exact response shapes, the finding schema, a Python example, and error codes.
              </div>
              <Link to="/api-docs" className="btn-primary flex-none px-3.5 py-2 text-[13px]">Open API Docs →</Link>
            </div>

            {/* Honesty */}
            <H id="honesty" icon={ShieldCheck}>The honesty principle</H>
            <p>
              Paxley reports only persisted scanner evidence. Anything it did not measure is labeled
              explicitly, and absence of a finding is stated as limited coverage — never proof of absence.
              Benchmark percentiles are relative to your own scanned portfolio, not an industry cohort.
            </p>
            <div className="mt-4 space-y-2 rounded-xl border border-brand/25 bg-brand/5 p-4 text-[13px] leading-6 text-text2">
              <p>Evidence mapping supports control review. It is not certification, an audit opinion, or proof of operating effectiveness.</p>
              <p>Automated license classification is not legal advice.</p>
            </div>
            <p className="mt-4 text-sm text-muted">
              That discipline is the point: a report built on Paxley is defensible precisely because it
              distinguishes what was measured from what was not.
            </p>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
}
